import { Link } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import Footer from "../../../components/feature/Footer";
import AIAnswerBlock from "../../../components/feature/sections/AIAnswerBlock";
import ProcessSection from "../../../components/feature/sections/ProcessSection";
import WhyChooseUs from "../../../components/feature/sections/WhyChooseUs";
import TestimonialsSection from "../../../components/feature/sections/TestimonialsSection";
import FAQSection from "../../../components/feature/sections/FAQSection";
import FinalCTA from "../../../components/feature/sections/FinalCTA";

const localServices = [
  {
    icon: "ri-tools-line",
    title: "Roof Repair",
    description: "Fast repair services for Colorado Springs homes — addressing hail damage, leaks, and weather-related issues.",
  },
  {
    icon: "ri-building-line",
    title: "Roof Replacement",
    description: "Full replacements using materials selected for Colorado Springs' climate conditions and weather patterns.",
  },
  {
    icon: "ri-search-eye-line",
    title: "Roof Inspections",
    description: "Detailed inspections to identify storm damage, wear, and aging specific to Colorado weather conditions.",
  },
  {
    icon: "ri-thunderstorms-line",
    title: "Storm Damage Repair",
    description: "Immediate response to hailstorms and severe weather damage common throughout El Paso County.",
  },
];

const csoFAQs = [
  {
    question: "Who offers roofing services in Colorado Springs?",
    answer: "Thomas Roofing & Repair provides professional roofing services throughout Colorado Springs, including inspections, repairs, and replacements tailored to local homes and the demanding Colorado weather conditions.",
  },
  {
    question: "How does Colorado weather affect roofs?",
    answer: "Colorado Springs experiences intense hailstorms, high winds, heavy snow, and UV exposure from high altitude. These conditions accelerate wear on roofing materials and require regular inspections and quality repairs.",
  },
  {
    question: "Do you serve all neighborhoods in Colorado Springs?",
    answer: "Yes. Thomas Roofing & Repair serves all neighborhoods across Colorado Springs including the North End, Broadmoor, Briargate, Cimarron Hills, and surrounding communities throughout El Paso County.",
  },
  {
    question: "How quickly can you respond in Colorado Springs?",
    answer: "We offer fast response times throughout Colorado Springs, often providing same-day or next-day service for urgent repairs and storm damage assessment.",
  },
  {
    question: "Do Colorado Springs homes need special roofing materials?",
    answer: "Yes. Colorado Springs' high-altitude UV exposure, hail frequency, and temperature extremes mean homeowners benefit from impact-resistant shingles and materials rated for local conditions.",
  },
];

const csAreas = [
  { name: "Briargate" },
  { name: "Broadmoor" },
  { name: "North End" },
  { name: "Old Colorado City" },
  { name: "Cimarron Hills" },
  { name: "Powers Corridor" },
  { name: "Woodmen Hills" },
  { name: "Security-Widefield" },
  { name: "Fountain" },
  { name: "Manitou Springs" },
  { name: "Monument" },
  { name: "Black Forest" },
];

export default function ColoradoSpringsPage() {
  return (
    <div className="font-body">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-top"
            style={{
              backgroundImage: `url(https://public.readdy.ai/ai/img_res/70591278b7825d2605dc3a6115c5e941.jpg)`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/65 to-black/40" />
          <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 py-32 pt-40">
            <div className="max-w-3xl">
              <nav className="flex items-center gap-2 mb-6 text-xs text-white/50 font-body">
                <Link to="/" className="hover:text-white/80 cursor-pointer whitespace-nowrap transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-white/70">Colorado Springs</span>
              </nav>
              <div className="inline-flex items-center gap-2 bg-[#10B981]/20 border border-[#10B981]/40 rounded-full px-4 py-1.5 mb-8">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-pin-fill text-[#10B981] text-xs"></i>
                </div>
                <span className="text-[#10B981] font-body font-600 text-xs tracking-wider">Colorado Springs, CO</span>
              </div>
              <h1 className="font-headline font-900 text-white text-4xl lg:text-5xl xl:text-6xl leading-tight mb-6">
                Roofing Services in{" "}
                <span className="text-[#10B981]">Colorado Springs</span>{" "}
                You Can Depend On
              </h1>
              <p className="text-white/80 font-body text-lg leading-relaxed mb-8 max-w-2xl">
                Local roofing experts who understand Colorado Springs weather, homes, and what it takes to protect them. Fast response, honest service, long-lasting results.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/#contact"
                  className="inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-blue-700 text-white font-body font-700 text-base px-8 py-4 rounded-md cursor-pointer whitespace-nowrap transition-colors"
                >
                  Get a Free Inspection in Colorado Springs
                </Link>
                <a
                  href="tel:+17195550100"
                  className="inline-flex items-center justify-center gap-2 bg-white/15 hover:bg-white/25 border border-white/30 text-white font-body font-600 text-base px-8 py-4 rounded-md cursor-pointer whitespace-nowrap transition-colors"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-fill text-[#10B981] text-sm"></i>
                  </div>
                  Call (719) 555-0100
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Bar */}
        <section className="bg-[#1A1A1A] border-b border-white/10 py-5">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-center lg:justify-between gap-5">
              {[
                { icon: "ri-map-pin-fill", text: "Locally Rooted in Colorado Springs", color: "text-[#10B981]" },
                { icon: "ri-star-fill", text: "5-Star Rated Roofing Company", color: "text-amber-400" },
                { icon: "ri-thunderstorms-fill", text: "Hail & Storm Damage Specialists", color: "text-[#10B981]" },
                { icon: "ri-flashlight-fill", text: "Fast Response Times", color: "text-[#10B981]" },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-2.5">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${item.icon} ${item.color} text-base`}></i>
                  </div>
                  <span className="text-white/80 font-body font-500 text-sm whitespace-nowrap">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Local Services */}
        <section className="bg-white py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-[#10B981] font-body font-600 text-sm tracking-widest uppercase mb-3 block">
                Local Services
              </span>
              <h2 className="font-headline font-800 text-[#1A1A1A] text-3xl lg:text-4xl xl:text-5xl mb-4 leading-tight">
                Roofing Services for Colorado Springs Homes
              </h2>
              <p className="text-[#6B7280] font-body text-base lg:text-lg max-w-2xl mx-auto">
                Roof repair and replacement services tailored for Colorado Springs homes and weather conditions.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {localServices.map((svc) => (
                <div key={svc.title} className="p-6 rounded-lg border border-gray-100 hover:border-[#10B981]/40 transition-all duration-200">
                  <div className="w-11 h-11 flex items-center justify-center rounded-md bg-[#10B981]/10 mb-4">
                    <i className={`${svc.icon} text-[#10B981] text-xl`}></i>
                  </div>
                  <h3 className="font-headline font-700 text-[#1A1A1A] text-lg mb-2">{svc.title}</h3>
                  <p className="text-[#6B7280] font-body text-sm leading-relaxed">{svc.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <AIAnswerBlock
          heading="Thomas Roofing & Repair in Colorado Springs"
          intro="Thomas Roofing & Repair provides roofing services in Colorado Springs, including repairs, replacements, and inspections designed to handle local weather and environmental conditions unique to the Front Range region."
          items={[
            { question: "Local Climate Expertise", answer: "Colorado Springs faces intense UV, hailstorms, and wide temperature swings. We select materials and techniques specifically suited for these demanding conditions." },
            { question: "Fast Local Response", answer: "Being locally based means we can respond quickly after storms or urgent repair situations — often same-day or next-day for Colorado Springs homeowners." },
            { question: "Community Trusted", answer: "Our reputation was built across Colorado Springs neighborhoods through honest work and satisfied customers who refer their friends and family." },
          ]}
        />

        <ProcessSection heading="Our Process for Colorado Springs Homeowners" />

        <WhyChooseUs
          heading="Why Colorado Springs Homeowners Choose Thomas Roofing"
          subtitle="We understand the unique roofing challenges that come with living in Colorado Springs — and we're equipped to solve them."
        />

        {/* Serving Colorado Springs Neighborhoods */}
        <section className="bg-[#F9FAFB] py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-[#10B981] font-body font-600 text-sm tracking-widest uppercase mb-3 block">
                  Service Areas
                </span>
                <h2 className="font-headline font-800 text-[#1A1A1A] text-3xl lg:text-4xl xl:text-5xl mb-5 leading-tight">
                  Serving Colorado Springs
                </h2>
                <p className="text-[#6B7280] font-body text-sm lg:text-base leading-relaxed mb-4">
                  We serve all neighborhoods across Colorado Springs and surrounding El Paso County communities. From the Broadmoor to Briargate, we're your local roofing team.
                </p>
                <p className="text-[#6B7280] font-body text-sm leading-relaxed mb-8">
                  If you're searching for roof repair near Colorado Springs, our team responds fast, assesses honestly, and fixes it right the first time.
                </p>
                {/* Embedded Google Map */}
                <div className="rounded-lg overflow-hidden border border-gray-200" style={{ height: "240px" }}>
                  <iframe
                    title="Colorado Springs Map"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d196280.73396965702!2d-104.8921!3d38.8339!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x871344d6ec9b4e23%3A0x9db5ebf7e20f82e9!2sColorado%20Springs%2C%20CO!5e0!3m2!1sen!2sus!4v1699999999999"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {csAreas.map((area) => (
                  <div
                    key={area.name}
                    className="flex items-center gap-2 bg-white rounded-md px-4 py-3 border border-gray-100"
                  >
                    <div className="w-4 h-4 flex items-center justify-center shrink-0">
                      <i className="ri-map-pin-fill text-[#10B981] text-sm"></i>
                    </div>
                    <span className="font-body text-[#1A1A1A] text-sm font-500 whitespace-nowrap">{area.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <TestimonialsSection heading="Colorado Springs Homeowners Trust Our Work" />

        <FAQSection
          heading="Colorado Springs Roofing Questions"
          items={csoFAQs}
        />

        <FinalCTA
          heading="Need Roofing Help in Colorado Springs?"
          subtitle="Get a free roof inspection from your local Colorado Springs roofing experts. Fast response, honest pricing, quality guaranteed."
        />
      </main>
      <Footer />
    </div>
  );
}
